package org.example.project.components

import androidx.compose.foundation.layout.*
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.ContentCopy
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.example.project.CheckLicenseKeyResult
import org.example.project.FirebaseManager
import org.example.project.PrimaryColor
import org.example.project.LicenseKeyPublicInfo
import org.example.project.LicenseKeyStatus

@Composable
fun CheckLicenseKeySection() {
    val scope = rememberCoroutineScope()
    var checkCodeSn by remember { mutableStateOf("") }
    var isCheckingCode by remember { mutableStateOf(false) }
    var checkCodeResultInfo by remember { mutableStateOf<LicenseKeyPublicInfo?>(null) }
    var checkCodeMessage by remember { mutableStateOf<String?>(null) }

    Card(
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(modifier = Modifier.padding(24.dp).fillMaxWidth()) {
            Text(
                text = "Verify License Key",
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = Color(0xFF64748B) // Slate 500
            )
            Spacer(modifier = Modifier.height(16.dp))
            OutlinedTextField(
                value = checkCodeSn,
                onValueChange = { 
                    checkCodeSn = it
                    if (it.isEmpty()) {
                        checkCodeResultInfo = null
                        checkCodeMessage = null
                    }
                },
                label = { Text("Serial Number") },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = PrimaryColor,
                    unfocusedBorderColor = Color(0xFFE2E8F0)
                )
            )
            
            Spacer(modifier = Modifier.height(12.dp))
            
            Button(
                onClick = {
                    if (checkCodeSn.isNotBlank()) {
                        scope.launch {
                            isCheckingCode = true
                            checkCodeMessage = null
                            checkCodeResultInfo = null
                            val result = FirebaseManager.checkLicenseKey(checkCodeSn)
                            when (result) {
                                is CheckLicenseKeyResult.Success -> {
                                    checkCodeResultInfo = result.data
                                }
                                is CheckLicenseKeyResult.Error -> {
                                    checkCodeMessage = result.message
                                }
                            }
                            isCheckingCode = false
                        }
                    }
                },
                enabled = checkCodeSn.isNotBlank() && !isCheckingCode,
                modifier = Modifier.fillMaxWidth(),
                colors = ButtonDefaults.buttonColors(containerColor = PrimaryColor),
                shape = MaterialTheme.shapes.medium
            ) {
                if (isCheckingCode) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(20.dp),
                        color = Color.White,
                        strokeWidth = 2.dp
                    )
                } else {
                    Text("Verify", fontWeight = FontWeight.Bold)
                }
            }

            if (checkCodeResultInfo != null) {
                Spacer(modifier = Modifier.height(20.dp))
                
                Surface(
                    color = Color(0xFFF8FAFC), // Slate 50
                    shape = MaterialTheme.shapes.small,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Column(modifier = Modifier.padding(16.dp)) {
                        // Header Row: LicenseUnits & Status Badge
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text(
                                text = "${checkCodeResultInfo!!.bonus_units} Volume Licenses",
                                style = MaterialTheme.typography.titleMedium,
                                fontWeight = FontWeight.Bold,
                                color = PrimaryColor
                            )
                            
                            val (statusColor, statusBg) = when (checkCodeResultInfo!!.status) {
                                LicenseKeyStatus.ACTIVE -> Color(0xFF16A34A) to Color(0xFFF0FDF4)
                                LicenseKeyStatus.CLAIMED -> Color(0xFF64748B) to Color(0xFFF8FAFC)
                                LicenseKeyStatus.REVOKED -> Color(0xFFDC2626) to Color(0xFFFEF2F2)
                            }
                            
                            Surface(
                                color = statusBg,
                                shape = MaterialTheme.shapes.small,
                                border = androidx.compose.foundation.BorderStroke(1.dp, statusColor.copy(alpha = 0.2f))
                            ) {
                                Text(
                                    text = checkCodeResultInfo!!.status.value.uppercase(),
                                    color = statusColor,
                                    fontWeight = FontWeight.Bold,
                                    style = MaterialTheme.typography.labelSmall,
                                    modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp)
                                )
                            }
                        }
                        
                        Spacer(modifier = Modifier.height(16.dp))
                        
                        // SN Row
                        Text(
                            text = "Serial Number:",
                            style = MaterialTheme.typography.labelSmall,
                            color = Color(0xFF94A3B8)
                        )
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text(
                                text = checkCodeResultInfo!!.key_sn,
                                style = MaterialTheme.typography.bodyMedium.copy(fontFamily = androidx.compose.ui.text.font.FontFamily.Monospace),
                                color = Color(0xFF334155),
                                modifier = Modifier.weight(1f)
                            )
                            IconButton(
                                onClick = {
                                    try {
                                        window.navigator.clipboard.writeText(checkCodeResultInfo!!.key_sn)
                                    } catch (e: Exception) {
                                        println("Clipboard not supported or blocked: ${e.message}")
                                    }
                                },
                                modifier = Modifier.size(32.dp)
                            ) {
                                Icon(
                                    imageVector = Icons.Outlined.ContentCopy,
                                    contentDescription = "Copy SN",
                                    tint = PrimaryColor,
                                    modifier = Modifier.size(16.dp)
                                )
                            }
                        }
                    }
                }
            }

            if (checkCodeMessage != null) {
                Spacer(modifier = Modifier.height(16.dp))
                Surface(
                    color = Color(0xFFFEF2F2),
                    shape = MaterialTheme.shapes.small,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Row(
                        modifier = Modifier.padding(16.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text = checkCodeMessage!!,
                            color = Color(0xFFDC2626),
                            style = MaterialTheme.typography.bodySmall,
                            fontWeight = FontWeight.Medium
                        )
                    }
                }
            }
        }
    }
}
